import React, { useEffect, useImperativeHandle, useState } from 'react';
import { FullscreenIcon, MinimizeIcon } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Lineicons } from '@lineiconshq/react-lineicons';
import { XmarkStroke } from '@lineiconshq/free-icons';
import { useDialogStack } from '@/hooks/use-dialog-stack';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

export interface DialogV2Props {
  title: string;
  description?: string;
  open?: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  actions?: React.ReactNode;
  disableBackdropClose?: boolean;
  disableEscapeKeyDown?: boolean;
  showFullscreenToggle?: boolean;
  fullScreen?: boolean;
  onFullscreenToggle?: () => void;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  titleIcon?: React.ReactNode;
  classNameContent?: string;
  classNameContainer?: string;
  paperClassName?: string;
  fullScreenPaperClassName?: string;
  zIndex?: number;
}

const maxWidthValueMap: Record<Exclude<DialogV2Props['maxWidth'], false | undefined>, string> = {
  xs: '444px',
  sm: '600px',
  md: '900px',
  lg: '1200px',
  xl: '1536px'
};

const isSearchSuggestionInteraction = (event: Event) => {
  const target = event.target;

  return target instanceof Element && Boolean(target.closest('[data-search-suggestions-dropdown="true"]'));
};

export const DialogV2 = React.forwardRef<{ open: () => void }, DialogV2Props>(
  (
    {
      title,
      description,
      open = true,
      onClose,
      children,
      actions,
      disableBackdropClose = false,
      disableEscapeKeyDown = false,
      showFullscreenToggle = true,
      fullScreen: propFullScreen = false,
      onFullscreenToggle,
      maxWidth = 'md',
      titleIcon,
      classNameContent,
      classNameContainer,
      paperClassName,
      fullScreenPaperClassName,
      zIndex
    },
    ref
  ) => {
    const isMobile = useIsMobile();
    const [isOpen, setIsOpen] = useState(open);
    const [isFullScreen, setIsFullScreen] = useState(propFullScreen);

    useEffect(() => {
      setIsOpen(open);
    }, [open]);

    useEffect(() => {
      setIsFullScreen(propFullScreen);
    }, [propFullScreen]);

    const openDialog = () => setIsOpen(true);
    const toggleFullScreen = () => {
      setIsFullScreen(current => !current);
      onFullscreenToggle?.();
    };

    useImperativeHandle(ref, () => ({ open: openDialog }), []);

    const { contentZIndex } = useDialogStack(isOpen);

    const shouldBeFullScreen = isMobile || isFullScreen;
    const maxWidthValue =
      maxWidth === false ? 'calc(100dvw - 64px)' : `min(${maxWidthValueMap[maxWidth]}, calc(100dvw - 64px))`;
    const finalZIndex = zIndex ?? contentZIndex;
    const cssVars = { '--arito-dialog-z-index': finalZIndex } as React.CSSProperties;
    const contentStyle = {
      ...cssVars,
      maxWidth: shouldBeFullScreen ? undefined : maxWidthValue
    } as React.CSSProperties;

    const requestClose = () => {
      if (!disableBackdropClose) {
        onClose?.();
      }
    };

    return (
      <DialogPrimitive.Root
        open={isOpen}
        onOpenChange={nextOpen => {
          if (nextOpen) {
            setIsOpen(true);
            return;
          }

          requestClose();
        }}
      >
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className={cn('fixed inset-0 z-[var(--arito-dialog-z-index,50)] bg-black/50', classNameContainer)}
            style={cssVars}
          />
          <DialogPrimitive.Content
            onEscapeKeyDown={event => {
              if (disableEscapeKeyDown) {
                event.preventDefault();
              }
            }}
            onPointerDownOutside={event => {
              if (disableBackdropClose || isSearchSuggestionInteraction(event.detail.originalEvent)) {
                event.preventDefault();
              }
            }}
            onInteractOutside={event => {
              if (disableBackdropClose || isSearchSuggestionInteraction(event.detail.originalEvent)) {
                event.preventDefault();
              }
            }}
            className={cn(
              'fixed left-1/2 top-1/2 z-[var(--arito-dialog-z-index,50)] flex max-h-[calc(100dvh-24px)] w-fit min-w-0 -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden border border-gray-200 bg-white shadow-lg outline-none',
              shouldBeFullScreen && 'inset-0 left-0 top-0 h-dvh max-h-dvh w-dvw max-w-none translate-x-0 translate-y-0',
              shouldBeFullScreen ? fullScreenPaperClassName : paperClassName
            )}
            style={contentStyle}
            data-arito-dialog-fullscreen={shouldBeFullScreen ? 'true' : undefined}
          >
            <DialogPrimitive.Title className='flex h-[var(--erp-dialog-header-height)] min-h-[var(--erp-dialog-header-height)] flex-none items-center justify-between border-b border-gray-300 bg-[#f2f7fc] px-2 py-1 text-black'>
              <div className='flex min-w-0 items-center'>
                {titleIcon}
                <span
                  className={cn('truncate text-[length:var(--erp-dialog-title-size)] font-medium', titleIcon && 'ml-2')}
                >
                  {title}
                </span>
              </div>
              <div className='ml-auto flex'>
                {showFullscreenToggle && !isMobile && (
                  <button
                    type='button'
                    onClick={toggleFullScreen}
                    title={isFullScreen ? 'Exit fullscreen' : 'Fullscreen'}
                    className='mr-1 inline-flex h-8 w-8 items-center justify-center rounded-sm text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-sky-500'
                  >
                    {isFullScreen ? <MinimizeIcon size={18} /> : <FullscreenIcon size={18} />}
                  </button>
                )}
                <button
                  type='button'
                  onClick={() => onClose?.()}
                  title='Close'
                  className='inline-flex h-8 w-8 items-center justify-center rounded-sm text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-sky-500'
                >
                  <Lineicons aria-hidden='true' icon={XmarkStroke} />
                </button>
              </div>
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className='sr-only'>{description || title}</DialogPrimitive.Description>

            <div
              className={cn(
                'flex min-h-0 flex-1 flex-col overflow-auto p-0',
                shouldBeFullScreen &&
                  '[&_[data-arito-form-v2]]:h-full [&_[data-arito-form-v2]]:min-h-full [&_[data-arito-form-v2]]:w-full [&_[data-arito-form-v2]]:max-w-none [&_[data-arito-form-v2]]:basis-full',
                classNameContent
              )}
            >
              {children}
            </div>

            {actions && (
              <div className='flex h-[52px] min-h-[52px] items-center justify-end gap-2 border-t border-gray-200 bg-[#f2f7fc] px-4 py-1.5'>
                {actions}
              </div>
            )}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    );
  }
);

DialogV2.displayName = 'DialogV2';

export default DialogV2;
